// src/components/Navbar.tsx
"use client";


import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { Search, Heart, ShoppingBag, X } from 'lucide-react';
import { useUser } from "@/providers/userContext";
import { getStoreTitle } from "@/services/storeService";
import { AxiosPost_Nocatch } from "@/utilities/axioscall";
import { AK } from "@/constants";
import SearchBar from "../themes/SearchBar";
import Image from "next/image";
import SvgIcons from "../themes/SvgIcons";
import logo from "@/public/assets/images/logo.png";

const Navbar = ({ categoryListData }: any) => {
  const pathname = usePathname();
  const router = useRouter();
  const { logout, user, cartinfoData } = useUser();

  const [menuOpen, setMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [userData, setUserData] = useState<any>(user);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<number[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  // Memoize main categories
  const mainCategories = useMemo(
    () =>
      categoryListData
        ?.filter(
          (category: any) => category.parent_id === 0 && category.display_in_menu === 1
        )
        .sort((a: any, b: any) => a.id - b.id),
    [categoryListData]
  );

  const getSubcategoriesByParentId = useMemo(
    () => (parentId: number) =>
      categoryListData?.filter((cat: any) => cat.parent_id === parentId),
    [categoryListData]
  );

  useEffect(() => {
    document.body.style.overflow = menuOpen || isSearchOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [menuOpen, isSearchOpen]);

  useEffect(() => {
    setUserData(user ?? null);
  }, [user]);

  useEffect(() => {
    setCartCount(cartinfoData?.cartcount ?? 0);
  }, [cartinfoData]);

  const hiddenPaths = ["/signin", "/signin/email"];
  const shouldHideNavbar =
    hiddenPaths.includes(pathname) ||
    pathname.startsWith("/cart") ||
    pathname.startsWith("/checkout");

  const handleNavigate = (path: string) => {
    router.push(path);
    setMenuOpen(false);
  };

  const toggleSubcategory = (categoryId: number) => {
    setExpandedCategoryIds((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleSearch = () => setIsSearchOpen((prev) => !prev);

  const handleLogout = async () => {
    try {
      await AxiosPost_Nocatch(AK.LOGOUTAPI, {}, true);
      logout();
      router.push("/signin");
    } catch {
      logout();
      router.push("/signin");
    }
  };

  if (shouldHideNavbar) return null;

  return (
    <>
      {/* Main Navbar */}
      <nav className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link href="/" className="flex-shrink-0">
              <Image
                src={logo}
                alt={getStoreTitle() || "Logo"}
                width={180}
                height={60}
                className="w-auto h-10 md:h-12 object-contain"
                priority
                onError={(e) => {
                  e.currentTarget.src =
                    "https://blr1.digitaloceanspaces.com/assetsvilva/vilvabusiness/website/common/otherallimages/empty-logowebp.webp";
                }}
              />
            </Link>

            {/* Desktop Search - md and up */}
            <div className="hidden md:block flex-1 max-w-xl mx-8">
              <SearchBar isMobile={false} onNavigate={handleNavigate} />
            </div>

            {/* Desktop Actions - md and up */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              <Link
                href="/my-wishlist"
                className="flex items-center gap-2 text-[#1A3A8A] hover:opacity-80 transition"
              >
                <SvgIcons.Wishlisticon />
                <span className="text-sm font-medium hidden lg:inline">Wishlist</span>
              </Link>

              <Link
                href="/cart"
                className="relative flex items-center gap-2 text-[#1A3A8A] hover:opacity-80 transition"
              >
                <div className="relative">
                  <SvgIcons.Bagicon />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full min-w-[18px] h-5 flex items-center justify-center px-1 border-2 border-white">
                      {cartCount}
                    </span>
                  )}
                </div>
                <span className="text-sm font-medium hidden lg:inline">Shop</span>
              </Link>

              {userData ? (
                <div className="relative">
                  <button
                    onMouseEnter={() => setIsOpen(true)}
                    onMouseLeave={() => setIsOpen(false)}
                    className="flex items-center gap-2 text-[#1A3A8A] hover:opacity-80"
                  >
                    <SvgIcons.Usericon />
                    <span className="text-sm font-medium hidden lg:inline">
                      {userData.name || "Account"}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border z-50">
                      <Link
                        href="/my-account"
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setIsOpen(false)}
                      >
                        My Account
                      </Link>
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsOpen(false);
                        }}
                        className="block w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-gray-50"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  href="/signin"
                  className="flex items-center gap-2 text-[#1A3A8A] hover:opacity-80 transition"
                >
                  <SvgIcons.Usericon />
                  <span className="text-sm font-medium hidden lg:inline">Login</span>
                </Link>
              )}
            </div>

            {/* Mobile Actions - sm and below */}
            <div className="flex md:hidden items-center gap-4 sm:gap-5">
              

              <button onClick={() => setMenuOpen(true)} aria-label="Menu">
                <div className="space-y-1.5">
                  <span className="block w-6 h-0.5 bg-[#1A3A8A]"></span>
                  <span className="block w-6 h-0.5 bg-[#1A3A8A]"></span>
                  <span className="block w-6 h-0.5 bg-[#1A3A8A]"></span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Category Bar - visible from md upwards, centered */}
      <div className="hidden md:block bg-primary text-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex justify-center gap-8 lg:gap-12">
            <Link href="/" className="flex flex-col items-center group">
              <div className="bg-white p-3 rounded-lg mb-2 transition-transform group-hover:scale-105">
                <SvgIcons.NavHome />
              </div>
              <span className="text-sm font-medium">Home</span>
            </Link>

            <Link href="/about" className="flex flex-col items-center group">
              <div className="bg-white p-4 rounded-lg mb-2 transition-transform group-hover:scale-105">
                <SvgIcons.About />
              </div>

              <span className="text-sm font-medium">About</span>
            </Link>

            {mainCategories?.slice(0, 4).map((category: any, index: number) => {
              const icons = [
                SvgIcons.BabyCareicon,
                SvgIcons.BabyToyicon,
                SvgIcons.ForMomsicon,
                SvgIcons.ForMomsicon,
              ];

              const Icon = icons[index] || SvgIcons.ForMomsicon;

              return (
                <Link
                  key={category.id}
                  href={`/products/${category.seo_url}`}
                  className="flex flex-col items-center group"
                >
                  <div className="bg-white p-3 rounded-lg mb-2 transition-transform group-hover:scale-105">
                    <Icon />
                  </div>
                  <span className="text-sm font-medium text-center">
                    {category.name}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {menuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50 md:hidden"
            onClick={() => setMenuOpen(false)}
          />
          <div
            className={`fixed inset-y-0 left-0 z-50 w-4/5 max-w-sm bg-white shadow-2xl transform transition-transform duration-300 md:hidden ${menuOpen ? "translate-x-0" : "-translate-x-full"
              }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Menu Header */}
            <div className="p-5 border-b flex items-center justify-between">
              <Image
                src={logo}
                alt="Logo"
                width={140}
                height={50}
                className="h-10 object-contain"
              />

              <button onClick={toggleSearch} aria-label="Search">
                <Search className="w-6 h-6 text-[#1A3A8A]" />
              </button>

              <Link href="/my-wishlist" aria-label="Wishlist">
                <Heart className="w-6 h-6 text-[#1A3A8A]" />
              </Link>

              <Link href="/cart" className="relative" aria-label="Cart">
                <ShoppingBag className="w-6 h-6 text-[#1A3A8A]" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-bold rounded-full h-5 min-w-[18px] flex items-center justify-center border-2 border-white">
                    {cartCount}
                  </span>
                )}
              </Link>
              
              <button onClick={() => setMenuOpen(false)}>
                <X size={28} className="text-gray-700" />
              </button>
            </div>

            {/* Menu Content */}
            <div className="overflow-y-auto h-[calc(100%-80px)] p-5">
              {/* User Section */}
              {userData ? (
                <div className="py-4 border-b">
                  <p className="font-medium">Hi, {userData.name}</p>
                  <Link
                    href="/my-account"
                    className="text-sm text-blue-600 mt-1 block"
                    onClick={() => setMenuOpen(false)}
                  >
                    My Account
                  </Link>
                </div>
              ) : (
                <Link
                  href="/signin"
                  className="block py-4 text-blue-600 font-medium border-b"
                  onClick={() => setMenuOpen(false)}
                >
                  Sign In / Register
                </Link>
              )}

              {/* Navigation */}
              <ul className="py-4 space-y-3">
                <li>
                  <Link href="/" className="block py-2 text-lg" onClick={() => setMenuOpen(false)}>
                    Home
                  </Link>
                </li>
                {mainCategories?.map((cat: any) => (
                  <li key={cat.id}>
                    <div className="flex justify-between items-center py-2">
                      <Link
                        href={`/products/${cat.seo_url}`}
                        className="text-lg"
                        onClick={() => setMenuOpen(false)}
                      >
                        {cat.name}
                      </Link>
                      {getSubcategoriesByParentId(cat.id).length > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSubcategory(cat.id);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={expandedCategoryIds.includes(cat.id) ? faChevronDown : faChevronRight}
                          />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}

      {/* Mobile-only Search Overlay */}
      {isSearchOpen && (
        <div className="fixed inset-0 z-50 md:hidden bg-black/70">
          <div className="absolute top-0 left-0 right-0 p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <SearchBar
                  isMobile={true}
                  onNavigate={(path) => {
                    handleNavigate(path);
                    toggleSearch();
                  }}
                />
              </div>
              <button onClick={toggleSearch}>
                <X size={24} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;